import { useState, useMemo } from 'react'
import { useAppStore } from '@/store/useAppStore'
import { getDueDateBucket, formatDate, cn } from '@/lib/utils'
import { X, User, Calendar, AlertCircle, CheckSquare, Clock } from 'lucide-react'
import { motion, AnimatePresence } from 'framer-motion'
import { Avatar } from '@/components/ui/Avatar'

const PERIOD_LABELS = { today: 'Today', week: 'This Week', month: 'This Month', all: 'All Assigned' }

function isThisWeek(dateStr) {
  if (!dateStr) return false
  const d = new Date(dateStr)
  const now = new Date()
  const startOfWeek = new Date(now)
  startOfWeek.setHours(0, 0, 0, 0)
  startOfWeek.setDate(now.getDate() - now.getDay())
  const endOfWeek = new Date(startOfWeek)
  endOfWeek.setDate(startOfWeek.getDate() + 7)
  return d >= startOfWeek && d < endOfWeek
}

function isThisMonth(dateStr) {
  if (!dateStr) return false
  const d = new Date(dateStr)
  const now = new Date()
  return d.getFullYear() === now.getFullYear() && d.getMonth() === now.getMonth()
}

function MemberCard({ member, tasks, isSelected, onClick }) {
  const incomplete = tasks.filter((t) => !t.completed)
  const overdue = incomplete.filter((t) => t.dueDate && getDueDateBucket(t.dueDate) === 'overdue')
  const dueToday = incomplete.filter((t) => t.dueDate && getDueDateBucket(t.dueDate) === 'today')

  return (
    <button
      onClick={onClick}
      className={cn(
        'w-full flex items-center gap-3 px-4 py-3 text-left transition-colors rounded-xl',
        isSelected ? 'bg-accent/10 border border-accent/30' : 'hover:bg-surface-raised border border-transparent'
      )}
    >
      <Avatar user={member} size="xl" />
      <div className="flex-1 min-w-0">
        <p className="text-sm font-medium text-text truncate">{member.name}</p>
        <p className="text-[11px] text-text-faint mt-0.5">{incomplete.length} open task{incomplete.length !== 1 ? 's' : ''}</p>
      </div>
      <div className="flex gap-1.5 flex-shrink-0">
        {overdue.length > 0 && (
          <span className="text-[10px] font-semibold px-1.5 py-0.5 rounded-md" style={{ background: 'rgba(255,59,59,0.12)', color: '#ff3b3b' }}>
            {overdue.length} overdue
          </span>
        )}
        {dueToday.length > 0 && (
          <span className="text-[10px] font-semibold px-1.5 py-0.5 rounded-md" style={{ background: 'rgba(0,208,132,0.12)', color: '#00d084' }}>
            {dueToday.length} today
          </span>
        )}
      </div>
    </button>
  )
}

function TaskLine({ task, project }) {
  const bucket = task.dueDate ? getDueDateBucket(task.dueDate) : null
  const isOverdue = bucket === 'overdue'
  const isToday = bucket === 'today'
  const { setSelectedTask } = useAppStore()

  return (
    <button
      onClick={() => setSelectedTask(task.id)}
      className="w-full flex items-center gap-3 px-4 py-2.5 hover:bg-surface-raised transition-colors text-left group"
    >
      <div
        className="w-[14px] h-[14px] rounded-full border-2 flex-shrink-0"
        style={{ borderColor: isOverdue ? '#ff3b3b' : isToday ? '#00d084' : 'var(--color-border)' }}
      />
      <span className="flex-1 text-[13px] text-text truncate">{task.title}</span>
      {project && (
        <span className="text-[10px] text-text-faint flex-shrink-0 flex items-center gap-1">
          <span className="w-1.5 h-1.5 rounded-sm inline-block" style={{ background: project.color }} />
          {project.name}
        </span>
      )}
      {task.dueDate && (
        <span
          className="text-[10px] flex-shrink-0 font-medium"
          style={{ color: isOverdue ? '#ff3b3b' : isToday ? '#00d084' : 'var(--color-text-faint)' }}
        >
          {formatDate(task.dueDate)}
        </span>
      )}
    </button>
  )
}

export function WorkloadModal({ projectId, onClose, initialMemberId }) {
  const { _membersCache, tasks, projects } = useAppStore()
  const [selectedMemberId, setSelectedMemberId] = useState(initialMemberId || null)
  const [period, setPeriod] = useState('week')

  const members = useMemo(() => {
    const cache = _membersCache?.[projectId] || []
    return cache.map((m) => ({
      id: m.userId,
      userId: m.userId,
      name: m.name || m.email || 'Unknown',
      email: m.email,
      avatar: m.avatar,
    }))
  }, [_membersCache, projectId])

  const selectedMember = members.find((m) => m.id === selectedMemberId) || members[0]

  const memberTasks = useMemo(() => {
    if (!selectedMember) return []
    return tasks.filter((t) =>
      t.assigneeId === selectedMember.userId &&
      !t.parentId
    )
  }, [tasks, selectedMember])

  const filteredTasks = useMemo(() => {
    const incomplete = memberTasks.filter((t) => !t.completed)
    if (period === 'today') return incomplete.filter((t) => t.dueDate && getDueDateBucket(t.dueDate) === 'today')
    if (period === 'week') return incomplete.filter((t) => t.dueDate && isThisWeek(t.dueDate))
    if (period === 'month') return incomplete.filter((t) => t.dueDate && isThisMonth(t.dueDate))
    return incomplete
  }, [memberTasks, period])

  const overdueCount = memberTasks.filter((t) => !t.completed && t.dueDate && getDueDateBucket(t.dueDate) === 'overdue').length

  // Workload gauge: 0-20 tasks = range; color-code by density
  const gauge = Math.min(filteredTasks.length / 15, 1)
  const gaugeColor = gauge > 0.7 ? '#ff3b3b' : gauge > 0.4 ? '#ff9500' : '#00d084'

  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      transition={{ duration: 0.15 }}
      className="fixed inset-0 z-50 flex items-center justify-center p-4"
      style={{ background: 'rgba(0,0,0,0.5)' }}
      onClick={(e) => e.target === e.currentTarget && onClose()}
    >
      <motion.div
        initial={{ opacity: 0, scale: 0.97, y: 8 }}
        animate={{ opacity: 1, scale: 1, y: 0 }}
        exit={{ opacity: 0, scale: 0.97, y: 8 }}
        transition={{ duration: 0.2, ease: [0.23, 1, 0.32, 1] }}
        className="bg-surface border border-border rounded-2xl shadow-2xl shadow-black/40 w-full max-w-3xl max-h-[80vh] flex flex-col overflow-hidden"
      >
        {/* Header */}
        <div className="flex items-center justify-between px-6 py-4 border-b border-border flex-shrink-0">
          <div>
            <h2 className="text-base font-semibold text-text">Team Workload</h2>
            <p className="text-xs text-text-faint mt-0.5">
              {projects.find((p) => p.id === projectId)?.name || 'Project'} — task distribution by member
            </p>
          </div>
          <button onClick={onClose} className="w-8 h-8 rounded-lg flex items-center justify-center text-text-faint hover:text-text hover:bg-surface-raised transition-colors active:scale-[0.95]">
            <X size={16} />
          </button>
        </div>

        <div className="flex flex-1 min-h-0">
          {/* Left: member list */}
          <div className="w-56 border-r border-border flex-shrink-0 overflow-y-auto py-2">
            {members.length === 0 ? (
              <div className="flex flex-col items-center justify-center py-10 px-4 gap-2">
                <User size={18} className="text-text-faint" />
                <p className="text-xs text-text-faint text-center">No members loaded</p>
              </div>
            ) : members.map((m) => (
              <div key={m.id} className="px-2">
                <MemberCard
                  member={m}
                  tasks={tasks.filter((t) => t.assigneeId === m.userId && !t.parentId)}
                  isSelected={(selectedMember?.id || members[0]?.id) === m.id}
                  onClick={() => setSelectedMemberId(m.id)}
                />
              </div>
            ))}
          </div>

          {/* Right: task detail for selected member */}
          <div className="flex-1 min-w-0 flex flex-col overflow-hidden">
            {selectedMember ? (
              <>
                {/* Member header */}
                <div className="px-6 py-4 border-b border-border flex-shrink-0">
                  <div className="flex items-center gap-3 mb-3">
                    <Avatar user={selectedMember} size="xl" />
                    <div>
                      <p className="text-sm font-semibold text-text">{selectedMember.name}</p>
                      <p className="text-[11px] text-text-faint">{memberTasks.filter((t) => !t.completed).length} open tasks</p>
                    </div>
                    {overdueCount > 0 && (
                      <span className="ml-auto flex items-center gap-1.5 text-[11px] font-medium px-2.5 py-1.5 rounded-lg" style={{ background: 'rgba(255,59,59,0.1)', color: '#ff3b3b' }}>
                        <AlertCircle size={11} />
                        {overdueCount} overdue
                      </span>
                    )}
                  </div>

                  {/* Workload gauge */}
                  <div className="mb-3">
                    <div className="flex items-center justify-between mb-1">
                      <span className="text-[10px] font-medium text-text-faint uppercase tracking-wider">Workload</span>
                      <span className="text-[10px] font-semibold tabular-nums" style={{ color: gaugeColor }}>
                        {filteredTasks.length} task{filteredTasks.length !== 1 ? 's' : ''}
                      </span>
                    </div>
                    <div className="h-1.5 rounded-full bg-border overflow-hidden">
                      <motion.div
                        className="h-full rounded-full"
                        animate={{ width: `${gauge * 100}%` }}
                        transition={{ duration: 0.4, ease: [0.23, 1, 0.32, 1] }}
                        style={{ background: gaugeColor }}
                      />
                    </div>
                  </div>

                  {/* Period tabs */}
                  <div className="flex gap-1">
                    {Object.entries(PERIOD_LABELS).map(([id, label]) => (
                      <button
                        key={id}
                        onClick={() => setPeriod(id)}
                        className={cn(
                          'text-[11px] font-medium px-2.5 py-1 rounded-lg transition-colors',
                          period === id ? 'bg-accent/15 text-accent' : 'text-text-faint hover:text-text'
                        )}
                      >
                        {label}
                      </button>
                    ))}
                  </div>
                </div>

                {/* Task list */}
                <div className="flex-1 overflow-y-auto py-1">
                  {filteredTasks.length === 0 ? (
                    <div className="flex flex-col items-center justify-center py-12 gap-3">
                      <div className="w-10 h-10 rounded-2xl bg-[#00d084]/10 flex items-center justify-center">
                        <CheckSquare size={18} style={{ color: '#00d084' }} />
                      </div>
                      <p className="text-sm font-medium text-text">Nothing due {PERIOD_LABELS[period].toLowerCase()}</p>
                      <p className="text-xs text-text-faint">
                        {period === 'all' ? 'No open tasks assigned' : 'No tasks scheduled for this period'}
                      </p>
                    </div>
                  ) : (
                    filteredTasks
                      .sort((a, b) => {
                        if (!a.dueDate && !b.dueDate) return 0
                        if (!a.dueDate) return 1
                        if (!b.dueDate) return -1
                        return new Date(a.dueDate) - new Date(b.dueDate)
                      })
                      .map((task) => (
                        <TaskLine
                          key={task.id}
                          task={task}
                          project={projects.find((p) => p.id === task.projectId)}
                        />
                      ))
                  )}
                </div>
              </>
            ) : (
              <div className="flex items-center justify-center h-full text-sm text-text-faint">
                Select a member to view workload
              </div>
            )}
          </div>
        </div>
      </motion.div>
    </motion.div>
  )
}
